import {Formik} from "formik";
import React, {FunctionComponent} from "react";
import {useIntl} from "react-intl";
import {BookingBillingValidationSchema} from "../../../constants/validation/BookingBillingValidationSchema";
import { BookingBillingRequest } from "../../../interfaces/BookingInterfaces";
import {bookingService} from "../../../services/BookingService";
import {toastUtils} from "../../../utils/toastUtils";
import EpowForm from "../form/EpowForm";
import FormFileUpload from "../input/FormFileUpload";
import FormInput from "../input/FormInput";
import GenericModal from "./GenericModal";

interface ModalBillBookingProps {
  className?: string,
  bookingId: string,
  modalOpen: boolean,
  onOpenModal: (open: boolean) => void,
  onDocumentAdded?: () => void,
}

const ModalBillBooking: FunctionComponent<ModalBillBookingProps> = ({
  className = "",
  bookingId,
  modalOpen,
  onOpenModal,
  onDocumentAdded,
}) => {
  const intl = useIntl()

  const handleBillCreation = (request: BookingBillingRequest) => {
    bookingService.addBookingDocument(
      bookingId,
      request.file,
      request.invoiceDate,
      request.number
    )
      .then(() => {
        toastUtils.successToast(intl.formatMessage({id: "success_toast_add_booking_file"}))
        onOpenModal(false)
        // Refresh booking details after successful upload
        if (onDocumentAdded) {
          onDocumentAdded()
        }
      })
      .catch(() => toastUtils.errorToast(intl.formatMessage({id: "error_toast_upload_file"})))
  }

  const initialValues: BookingBillingRequest = {
    bookingId: bookingId,
    number: null,
    invoiceDate: null,
    file: null,
  }

  return (
    <Formik initialValues={initialValues} validationSchema={BookingBillingValidationSchema} onSubmit={handleBillCreation}>
      {({errors, touched, values, setFieldValue, submitForm, resetForm}) => (
        <GenericModal
          className={className}
          title={intl.formatMessage({id: "booking_bill_create"})}
          open={modalOpen}
          onOpen={(open) => {
            if (!open) {
              resetForm();
            }
            onOpenModal(open);
          }}
          closable
          footer={{
            primaryAction: {
              buttonLabel: intl.formatMessage({id: "validate_button"}),
              action: () => submitForm()
            },
            secondaryAction: {
              buttonLabel: intl.formatMessage({id: "cancel_button"}),
              action: () => {
                resetForm();
                onOpenModal(false);
              }
            }
          }}
        >
          <EpowForm id={"booking_billing_form"} className={`m-4 ${className}`}>
            <FormInput
              id="number"
              label="booking_bill_number"
              value={values?.number}
              onChange={(e) => setFieldValue("number", e.target.value)}
              required
              error={errors.number}
              touched={touched.number}
            />
            <FormInput
              id="invoiceDate"
              label={intl.formatMessage({id: "booking_bill_date"})}
              type="date"
              value={values?.invoiceDate}
              onChange={(e) => setFieldValue("invoiceDate", e.target.value)}
              required
              error={errors.invoiceDate}
              touched={touched.invoiceDate}
            />
            <FormFileUpload
              id={"billId"}
              label="booking_form_supporting_field"
              required
              accept={[".pdf"]}
              onChange={(file: File) => setFieldValue("file", file)}
              error={errors.file}
              touched={touched.file}
            />
          </EpowForm>
        </GenericModal>
      )}
    </Formik>
  )
}

export default ModalBillBooking;
